import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { CardImg } from "reactstrap"
import AppsTrackingPlanModal from "../../components/Common/Modal/AppsTrackingPlanModal"
import { FaLock } from "react-icons/fa"
import { generateSlug } from "../../utils/seoUtils"
import { Info } from "lucide-react"
import { useAnalytics, ANALYTICS_EVENTS } from "../../analytics"
import "./TopAppCardM.css"

const LOCK_PLACEHOLDER = "%UPGRADE_KEYQ%"

const StatTile = ({ label, value, hint }) => (
  <div className="kq-app-card__stat">
    <span className="kq-app-card__stat-label">
      {label}
      {hint && (
        <span className="kq-app-card__stat-hint" title={hint}>
          <Info size={12} />
        </span>
      )}
    </span>
    <span className="kq-app-card__stat-value">{value}</span>
  </div>
)

export default function TopAppCardM({ gamedata, type, details, priceLayout, showEnds = true, showRevenue = true, showDownloads = true, headingLevel = "h3", insightType, sourceSurface, platformIcon, platformName, ...props }) {
  const CardHeadingTag = headingLevel
  const { trackEvent } = useAnalytics()
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)

  // Returns "N/A" instead of formatting missing/invalid data into `NaN` (which used
  // to render literally on screen), and — since "N/A" is truthy — this also removes
  // the need for the `|| "5.3 M"` / `|| "123212"` style fallbacks that used to show
  // a fake, plausible-looking number whenever the real value was missing.
  const formatNumber = num => {
    if (num === undefined || num === null || num === '' || isNaN(Number(num))) return 'N/A'
    if (num >= 1e6) {
      return (num / 1e6).toFixed(1) + " M" // Convert to millions
    } else if (num >= 1e3) {
      return (num / 1e3).toFixed(1) + " K" // Convert to thousands
    }
    return Math.round(num) // Return as is if less than 1000
  }

  // Wraps a stat's display value with the blur+lock treatment when the API
  // returned the upgrade placeholder instead of a real number.
  const renderStatValue = (rawValue, formatted) => {
    if (!String(rawValue ?? '').includes(LOCK_PLACEHOLDER)) return formatted
    return (
      <span
        className="kq-app-card__locked"
        onClick={(e) => { e.stopPropagation(); setShowUpgradeModal(true) }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setShowUpgradeModal(true) } }}
        role="button"
        tabIndex={0}
      >
        <span className="kq-app-card__locked-blur">{formatted}</span>
        <FaLock className="kq-app-card__lock-icon" aria-hidden="true" />
      </span>
    )
  }

  const renderPriceDisplay = (price) => {
    if (!price) return <span style={{ color: '#ffffff' }}>-</span>
    if (price.amount === 0) return <span style={{ color: '#ffffff', fontWeight: 700, fontSize: '15px' }}>Free</span>

    // Pico (and Steam-style) prices carry `currency` + `initial_amount` instead of Meta's `og_price`
    const currency = price.currency || price.og_price?.currency || 'USD'
    const formatAmount = (amt) => {
      if (currency === 'USD') return `$${amt}`
      try {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amt)
      } catch {
        return `${currency} ${amt}`
      }
    }
    const discounted = typeof price.amount === 'number' ? formatAmount(price.amount) : String(price.amount)
    const initial = Number(price.initial_amount)
    const original = price?.og_price?.formatted
      || (price.initial_amount != null && !isNaN(initial) && initial > Number(price.amount) ? formatAmount(initial) : null)
    // If this card is rendered for Top Discounts page, show original on left and discounted on right
    if (priceLayout === 'discounts') {
      if (original && String(original).trim() !== String(discounted).trim()) {
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#A1ACB8', textDecoration: 'line-through', fontSize: '13px' }}>{original}</span>
            <span style={{
              fontWeight: 700,
              fontSize: '15px',
              background: 'linear-gradient(90deg, #34d399 0%, #059669 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>{discounted}</span>
          </span>
        )
      }
      return (
        <span style={{ color: '#ffffff', fontWeight: 700, fontSize: '15px' }}>{discounted}</span>
      )
    }

    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span style={{ color: '#ffffff', fontWeight: 700, fontSize: '15px' }}>{discounted}</span>
        {original && String(original).trim() !== String(discounted).trim() && (
          <span style={{ color: '#A1ACB8', textDecoration: 'line-through', fontSize: '13px' }}>{original}</span>
        )}
      </span>
    )
  }

  const parseEndTime = (t) => {
    if (t === undefined || t === null) return null
    const n = Number(t)
    // if timestamp looks like ms (>= 1e12) use as-is, else treat as seconds
    return n > 1e12 ? n : n * 1000
  }

  const formatEndsIn = (endTimeMs) => {
    if (!endTimeMs) return null
    const now = Date.now()
    let diff = endTimeMs - now
    if (diff <= 0) return 'SALE_ENDED'
    const totalSeconds = Math.floor(diff / 1000)
    const days = Math.floor(totalSeconds / 86400)
    const hours = Math.floor((totalSeconds % 86400) / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    const pad = (n) => String(n).padStart(2, '0')
    const dayLabel = days === 1 ? 'day' : 'days'
    return `${days} ${dayLabel} and ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
  }

  // support multiple possible end-time keys from API
  const endTimeRaw = (
    (gamedata && gamedata.price && (
      gamedata.price.end_time ?? gamedata.price.ends_on ?? gamedata.price.ends_on_time ?? gamedata.price.ends ?? null
    )) || gamedata?.ends_on || null
  )

  // Live countdown state for discounts layout
  const [endsInText, setEndsInText] = useState(null)
  const [endTitle, setEndTitle] = useState('')

  useEffect(() => {
    if (priceLayout !== 'discounts' || !endTimeRaw) {
      setEndsInText(null)
      setEndTitle('')
      return
    }
    const endMs = parseEndTime(endTimeRaw)
    setEndTitle(endMs ? (new Date(endMs)).toLocaleString() : '')
    const update = () => {
      setEndsInText(formatEndsIn(endMs))
    }
    update()
    const iv = setInterval(update, 1000)
    return () => clearInterval(iv)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceLayout, endTimeRaw])

  const formatReleaseDate = (game) => {
    const iso = game?.created_at
    if (iso) {
      const d = new Date(iso)
      if (!isNaN(d)) return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    }
    const epoch = game?.create_time
    if (epoch) {
      const d = new Date(epoch * 1000)
      if (!isNaN(d)) return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
    }
    return "-"
  }

  const appSlug = generateSlug(gamedata?.name || '')
  const appLink = `/xr/app/${appSlug}?id=${gamedata?.item_id}`
  const hasRank = gamedata?.rank !== undefined && gamedata?.rank !== null

  const icon = (
    <CardImg
      src={gamedata?.image_url || "https://via.placeholder.com/50"}
      alt="App Icon"
    />
  )

  const endsInBadge = priceLayout === 'discounts' && showEnds !== false && endTimeRaw && endsInText ? (
    <div className="kq-app-card__ends" title={endTitle}>
      {endsInText === 'SALE_ENDED' ? (
        <span className="kq-app-card__ended-badge">⏳ Sale Ended</span>
      ) : (
        <>
          <span className="kq-app-card__ends-label">Ends in:</span>
          <span className="kq-app-card__ends-value">{endsInText}</span>
        </>
      )}
    </div>
  ) : null

  const cardContent = (
    <div
      className="app-card-container2"
      style={{
        border: "none",
        cursor: "pointer",
        transition: "box-shadow 0.3s ease",
        background: "transparent",
      }}
    >
      <div className="p-0">
        <div className="p-lg-4 p-2">
          <div className="kq-app-card">
            <div className="kq-app-card__top">
              <div className="kq-app-card__icon-wrap">
                {type === "HITS" ? (
                  <Link to={appLink} state={{ app_id: gamedata?.item_id, data: gamedata }} onClick={(e) => e.stopPropagation()}>
                    {icon}
                  </Link>
                ) : icon}
                {hasRank && <span className="kq-app-card__rank">#{gamedata.rank}</span>}
              </div>

              <div className="kq-app-card__info">
                <CardHeadingTag className="kq-app-card__title">
                  {gamedata?.name || "Demo App"}
                </CardHeadingTag>
                <p className="kq-app-card__meta">
                  {gamedata?.publisher || gamedata?.developer || "Unknown Publisher"}
                  <span className="kq-app-card__meta-dot">•</span>
                  {gamedata?.category || "Category"}
                </p>
                {platformName && (
                  <span className="kq-app-card__platform">
                    {platformIcon && <img src={platformIcon} alt={platformName} className="kq-app-card__platform-icon" />}
                    {platformName}
                  </span>
                )}
              </div>

              <div className="kq-app-card__price">
                <span className="kq-app-card__price-label">Price</span>
                {renderPriceDisplay(gamedata?.price)}
                {endsInBadge}
              </div>
            </div>

            <div className="kq-app-card__stats">
              {showRevenue && (
                <StatTile
                  label="Revenue"
                  hint="Add-ons revenue included in total"
                  value={renderStatValue(gamedata?.revenue, <>$ {formatNumber(gamedata?.revenue)}</>)}
                />
              )}
              {showDownloads && (
                <StatTile
                  label="Downloads"
                  value={renderStatValue(gamedata?.downloads, formatNumber(gamedata?.downloads))}
                />
              )}
              <StatTile
                label="Released"
                value={formatReleaseDate(gamedata)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // If we're in a context where we can use Link, wrap the card in a Link
  if (type === "HITS") {
    return (
      <>
        {cardContent}
        <AppsTrackingPlanModal show={showUpgradeModal} handleClose={() => setShowUpgradeModal(false)} />
      </>
    );
  }

  // Otherwise, just return the card
  return (
    <Link
      to={appLink}
      className=""
      state={{
        app_id: gamedata?.item_id,
        data: gamedata,
        ...(sourceSurface ? { source_surface: sourceSurface } : {}),
      }}
      onClick={() => {
        if (insightType) {
          trackEvent(ANALYTICS_EVENTS.FEATURE.WEEKLY_INSIGHT_APP_SELECTED, {
            app_id: gamedata?.item_id,
            app_name: gamedata?.name,
            insight_type: insightType,
          })
        }
      }}
    >
      {cardContent}
      <AppsTrackingPlanModal show={showUpgradeModal} handleClose={() => setShowUpgradeModal(false)} />
    </Link>
  );
}
